import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import {
  getDocumentPurchaseLines,
  getPurchaseLineFeatures,
  type PurchaseLineDTO,
} from "../../api/documents";
import {
  clearMatches,
  getDocumentMatches,
  getMatchSummary,
  matchOrderLines,
  removeMatch,
  setMatchQuantity,
  type MatchedLine,
  type MatchingMutationResult,
  type MatchSummary,
} from "../../api/purchaseMatching";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import {
  classifyBlockingReason,
  EMPTY_GUID,
  groupMatchedLines,
  isReceiptLevelMatch,
  type MatchBlockingReasonKind,
} from "../../utils/purchaseMatchingState";

type Props = {
  documentId: string;
  companyId: string;
  goodsInvoice: boolean;
  refreshKey: number;
  embedded?: boolean;
  onMatchingChanged?: () => void;
};

type PendingDelete =
  | {
      kind: "single";
      line: PurchaseLineDTO;
      orderLineId: string;
      receiptLineId?: string;
      label: string;
    }
  | {
      kind: "clear";
      line: PurchaseLineDTO;
      label: string;
    };

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("pl-PL", { maximumFractionDigits: 4 });
}

function quantityKey(
  lineSystemId: string,
  orderLineId: string,
  receiptLineId?: string
): string {
  return `${lineSystemId}|${orderLineId}|${receiptLineId || EMPTY_GUID}`;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function statusChip(
  line: PurchaseLineDTO,
  summary: MatchSummary | null | undefined
) {
  if (summary) {
    if (summary.ready) {
      return <Chip size="small" color="success" label="Gotowe" />;
    }

    if (summary.matched) {
      return <Chip size="small" color="warning" label="Niekompletne" />;
    }

    return <Chip size="small" variant="outlined" label="Brak dopasowania" />;
  }

  if (line.matchStatus === "Ready") {
    return <Chip size="small" color="success" label="Gotowe" />;
  }

  if (line.lineMatched || line.matchStatus === "Incomplete") {
    return <Chip size="small" color="warning" label="Niekompletne" />;
  }

  return <Chip size="small" variant="outlined" label="Brak dopasowania" />;
}

function blockingSeverity(kind: MatchBlockingReasonKind) {
  if (kind === "waitingForReceipt") return "info" as const;
  if (kind === "notMatched") return "info" as const;
  return "warning" as const;
}

export default function DocumentPurchaseMatchingState({
  documentId,
  companyId,
  goodsInvoice,
  refreshKey,
  embedded = false,
  onMatchingChanged,
}: Props) {
  const { t } = useAppTranslation("common");

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [lines, setLines] = useState<PurchaseLineDTO[]>([]);
  const [matchesByLine, setMatchesByLine] = useState<
    Record<string, MatchedLine[]>
  >({});
  const [summaries, setSummaries] = useState<
    Record<string, MatchSummary | null>
  >({});
  const [summaryErrors, setSummaryErrors] = useState<Record<string, string>>({});
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [busyLineId, setBusyLineId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setError(null);
      setSuccessMessage(null);
      setLoading(true);

      try {
        const features = await getPurchaseLineFeatures(companyId);
        if (!active) return;

        const matchingEnabled = features.usePurchOrderMatching && goodsInvoice;
        setEnabled(matchingEnabled);

        if (!matchingEnabled) {
          setLines([]);
          setMatchesByLine({});
          setSummaries({});
          setSummaryErrors({});
          setQuantityDrafts({});
          return;
        }

        const [lineRows, documentMatches] = await Promise.all([
          getDocumentPurchaseLines(companyId, documentId),
          getDocumentMatches(companyId, documentId),
        ]);

        if (!active) return;

        const nextMatches: Record<string, MatchedLine[]> = {};
        const nextDrafts: Record<string, string> = {};

        for (const match of documentMatches) {
          const current = nextMatches[match.invoiceLineId] ?? [];
          current.push(match);
          nextMatches[match.invoiceLineId] = current;
          nextDrafts[
            quantityKey(
              match.invoiceLineId,
              match.orderLineId,
              match.receiptLineId
            )
          ] = String(match.qtyToInvoice ?? 0);
        }

        const nextSummaries: Record<string, MatchSummary | null> = {};
        const nextSummaryErrors: Record<string, string> = {};

        await Promise.all(
          lineRows.map(async (line) => {
            if (!line.lineMatched && line.matchStatus === "NotMatched") {
              nextSummaries[line.systemId] = null;
              return;
            }

            try {
              nextSummaries[line.systemId] = await getMatchSummary(
                companyId,
                documentId,
                line.systemId
              );
            } catch (summaryError) {
              nextSummaries[line.systemId] = null;
              nextSummaryErrors[line.systemId] = errorMessage(
                summaryError,
                "Nie udało się pobrać podsumowania dopasowania wiersza."
              );
            }
          })
        );

        if (!active) return;

        setLines(lineRows);
        setMatchesByLine(nextMatches);
        setSummaries(nextSummaries);
        setSummaryErrors(nextSummaryErrors);
        setQuantityDrafts(nextDrafts);
      } catch (loadError) {
        console.error(loadError);
        if (!active) return;
        setError(
          errorMessage(
            loadError,
            "Nie udało się pobrać aktualnego stanu dopasowania ZZ/PZ."
          )
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [companyId, documentId, goodsInvoice, refreshKey]);

  const applyMutationResult = (
    lineSystemId: string,
    result: MatchingMutationResult,
    fallbackSuccess: string
  ) => {
    setMatchesByLine((current) => ({
      ...current,
      [lineSystemId]: result.matches ?? [],
    }));
    setSummaries((current) => ({
      ...current,
      [lineSystemId]: result.summary,
    }));
    setSummaryErrors((current) => {
      const next = { ...current };
      delete next[lineSystemId];
      return next;
    });
    setQuantityDrafts((current) => {
      const next = { ...current };
      for (const key of Object.keys(next)) {
        if (key.startsWith(`${lineSystemId}|`)) delete next[key];
      }
      for (const match of result.matches ?? []) {
        next[
          quantityKey(
            lineSystemId,
            match.orderLineId,
            match.receiptLineId
          )
        ] = String(match.qtyToInvoice ?? 0);
      }
      return next;
    });
    setSuccessMessage(result.message || fallbackSuccess);
    onMatchingChanged?.();
  };

  const saveQuantity = async (
    line: PurchaseLineDTO,
    match: MatchedLine
  ) => {
    const key = quantityKey(
      line.systemId,
      match.orderLineId,
      match.receiptLineId
    );
    const raw = quantityDrafts[key] ?? String(match.qtyToInvoice ?? 0);
    const value = Number(raw.replace(",", "."));

    if (!Number.isFinite(value) || value < 0) {
      setError(t("Podaj poprawną, nieujemną ilość do zafakturowania."));
      return;
    }

    setBusyLineId(line.systemId);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await setMatchQuantity(
        companyId,
        documentId,
        line.systemId,
        match.orderLineId,
        value,
        isReceiptLevelMatch(match) ? match.receiptLineId : undefined
      );
      applyMutationResult(
        line.systemId,
        result,
        t("Ilość dopasowania została zaktualizowana.")
      );
    } catch (saveError) {
      console.error(saveError);
      setError(
        errorMessage(
          saveError,
          t("Nie udało się zmienić ilości dopasowania.")
        )
      );
    } finally {
      setBusyLineId(null);
    }
  };

  const refreshReceiptMatches = async (line: PurchaseLineDTO) => {
    const groups = groupMatchedLines(matchesByLine[line.systemId] ?? []);
    const orderLineIds = groups.map((group) => group.orderLineId);

    if (orderLineIds.length === 0) return;

    setBusyLineId(line.systemId);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await matchOrderLines(
        companyId,
        documentId,
        line.systemId,
        orderLineIds
      );
      applyMutationResult(
        line.systemId,
        result,
        t("Dopasowanie zostało odświeżone o aktualne przyjęcia PZ.")
      );
    } catch (refreshError) {
      console.error(refreshError);
      setError(
        errorMessage(
          refreshError,
          t("Nie udało się odświeżyć dopasowania przyjęć PZ.")
        )
      );
    } finally {
      setBusyLineId(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;

    const { line } = pendingDelete;
    setBusyLineId(line.systemId);
    setError(null);
    setSuccessMessage(null);

    try {
      const result =
        pendingDelete.kind === "clear"
          ? await clearMatches(companyId, documentId, line.systemId)
          : await removeMatch(
              companyId,
              documentId,
              line.systemId,
              pendingDelete.orderLineId,
              pendingDelete.receiptLineId
            );

      applyMutationResult(
        line.systemId,
        result,
        pendingDelete.kind === "clear"
          ? t("Wszystkie dopasowania wiersza zostały usunięte.")
          : t("Dopasowanie zostało usunięte.")
      );
      setPendingDelete(null);
    } catch (deleteError) {
      console.error(deleteError);
      setError(
        errorMessage(
          deleteError,
          t("Nie udało się usunąć dopasowania.")
        )
      );
    } finally {
      setBusyLineId(null);
    }
  };

  const visibleLines = lines.filter((line) => {
    const lineMatches = matchesByLine[line.systemId] ?? [];
    const summary = summaries[line.systemId];

    return (
      lineMatches.length > 0 ||
      summary?.matched === true ||
      line.lineMatched === true
    );
  });

  if (enabled === false || !goodsInvoice) return null;

  return (
    <>
      <Paper
        variant={embedded ? "elevation" : "outlined"}
        elevation={0}
        sx={
          embedded
            ? { p: 0, bgcolor: "transparent" }
            : { p: 2, mt: 2 }
        }
      >
        {!embedded && (
          <>
            <Typography variant="subtitle1">{t("Zapisane dopasowania")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t(
                "Rozwiń wiersz, aby sprawdzić przypisane ZZ/PZ, zmienić ilości albo usunąć błędne dopasowania."
              )}
            </Typography>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {embedded && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "Każdy wiersz jest domyślnie zwinięty. Rozwiń go, aby zobaczyć strukturę ZZ → PZ i dostępne akcje."
            )}
          </Typography>
        )}

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2">{t("Pobieranie...")}</Typography>
          </Stack>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!loading && successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        )}

        {!loading && !error && enabled === true && visibleLines.length === 0 && (
          <Alert severity="info">
            {t(
              "Nie zapisano jeszcze żadnych dopasowań. Przejdź do zakładki Dopasuj wiersze, aby przypisać ZZ lub PZ."
            )}
          </Alert>
        )}

        {!loading && enabled === true && visibleLines.length > 0 && (
          <Stack spacing={1.25}>
            {visibleLines.map((line) => {
              const lineMatches = matchesByLine[line.systemId] ?? [];
              const summary = summaries[line.systemId];
              const groups = groupMatchedLines(lineMatches);
              const receiptCount = groups.reduce(
                (total, group) => total + group.receipts.length,
                0
              );
              const blockingKind = classifyBlockingReason(summary);
              const lineQuantity = summary?.lineQuantity ?? line.quantity ?? 0;
              const matchedQuantity =
                summary?.qtyToInvoice ?? line.matchedQtyToInvoice ?? 0;
              const difference =
                summary?.difference ?? lineQuantity - matchedQuantity;
              const busy = busyLineId === line.systemId;
              const ready = summary?.ready || line.matchStatus === "Ready";

              return (
                <Accordion
                  key={line.systemId}
                  variant="outlined"
                  disableGutters
                  sx={{
                    borderColor: ready ? "success.light" : "divider",
                    borderRadius: "8px !important",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 2,
                      bgcolor: ready ? "action.selected" : "action.hover",
                      "& .MuiAccordionSummary-content": { my: 1.25 },
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "flex-start", md: "center" }}
                      justifyContent="space-between"
                      sx={{ width: "100%", pr: 1, minWidth: 0 }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            {t("Wiersz")} {line.lineNo ?? "—"}
                          </Typography>
                          {statusChip(line, summary)}
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ mt: 0.25 }}
                        >
                          {line.description || "—"}
                        </Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        useFlexGap
                        flexWrap="wrap"
                        justifyContent={{ xs: "flex-start", md: "flex-end" }}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${t("Dopasowano")}: ${formatNumber(matchedQuantity)} / ${formatNumber(lineQuantity)} ${line.unitOfMeasureCode || ""}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${groups.length} ZZ • ${receiptCount} PZ`}
                        />
                        {Math.abs(difference) > 0.0000001 && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`${t("Różnica")}: ${formatNumber(difference)} ${line.unitOfMeasureCode || ""}`}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      {summaryErrors[line.systemId] && (
                        <Alert severity="warning">
                          {summaryErrors[line.systemId]}
                        </Alert>
                      )}

                      {summary && !summary.ready && summary.blockingReason && (
                        <Alert
                          severity={blockingSeverity(blockingKind)}
                          action={
                            blockingKind === "receiptRefresh" ? (
                              <Button
                                color="inherit"
                                size="small"
                                startIcon={<RefreshIcon />}
                                disabled={busy}
                                onClick={() => void refreshReceiptMatches(line)}
                              >
                                {t("Odśwież dopasowanie")}
                              </Button>
                            ) : undefined
                          }
                        >
                          {summary.blockingReason}
                        </Alert>
                      )}

                      {groups.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t("Do tego wiersza nie zapisano jeszcze żadnego dopasowania.")}
                        </Typography>
                      ) : (
                        <Stack spacing={1.5}>
                          {groups.map((group) => {
                            const order = group.order;
                            const canEditOrderQuantity =
                              order.receiptOnInvoice && group.receipts.length === 0;
                            const orderDraftKey = quantityKey(
                              line.systemId,
                              group.orderLineId,
                              order.receiptLineId
                            );

                            return (
                              <Paper
                                key={group.orderLineId}
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  bgcolor: "action.hover",
                                  borderLeft: 3,
                                  borderLeftColor: "primary.main",
                                }}
                              >
                                <Stack spacing={1.25}>
                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Stack
                                        direction="row"
                                        spacing={0.75}
                                        alignItems="center"
                                        useFlexGap
                                        flexWrap="wrap"
                                      >
                                        <Chip
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          label="ZZ"
                                        />
                                        <Typography variant="subtitle2" fontWeight={700}>
                                          {order.orderNo || "—"}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={`${t("wiersz")} ${order.orderLineNo || "—"}`}
                                        />
                                        {order.receiptOnInvoice && (
                                          <Chip
                                            size="small"
                                            color="info"
                                            label={t("Przyjęcie przy fakturze")}
                                          />
                                        )}
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                      >
                                        {order.description || order.no || "—"}
                                      </Typography>
                                    </Box>

                                    <Button
                                      size="small"
                                      color="error"
                                      variant="text"
                                      startIcon={<DeleteOutlineIcon />}
                                      disabled={busy}
                                      onClick={() =>
                                        setPendingDelete({
                                          kind: "single",
                                          line,
                                          orderLineId: group.orderLineId,
                                          label: `${t("zamówienie")} ${order.orderNo || "—"}`,
                                        })
                                      }
                                    >
                                      {t("Usuń ZZ")}
                                    </Button>
                                  </Stack>

                                  {canEditOrderQuantity && (
                                    <Stack
                                      direction={{ xs: "column", sm: "row" }}
                                      spacing={1}
                                      alignItems={{ sm: "center" }}
                                    >
                                      <TextField
                                        size="small"
                                        type="number"
                                        label={t("Ilość do zafakturowania")}
                                        value={
                                          quantityDrafts[orderDraftKey] ??
                                          String(order.qtyToInvoice ?? 0)
                                        }
                                        onChange={(event) =>
                                          setQuantityDrafts((current) => ({
                                            ...current,
                                            [orderDraftKey]: event.target.value,
                                          }))
                                        }
                                        inputProps={{ min: 0, step: "any" }}
                                        disabled={busy}
                                        sx={{ width: { xs: "100%", sm: 220 } }}
                                      />
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={busy}
                                        onClick={() => void saveQuantity(line, order)}
                                      >
                                        {t("Zapisz ilość")}
                                      </Button>
                                    </Stack>
                                  )}

                                  {group.receipts.length === 0 && !order.receiptOnInvoice && (
                                    <Alert severity="info">
                                      {t(
                                        "Dla tego zamówienia nie ma jeszcze dopasowanego zaksięgowanego przyjęcia PZ."
                                      )}
                                    </Alert>
                                  )}

                                  {group.receipts.length > 0 && (
                                    <Stack spacing={1}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight={600}
                                      >
                                        {t("Przyjęcia PZ")}
                                      </Typography>

                                      {group.receipts.map((receipt) => {
                                        const draftKey = quantityKey(
                                          line.systemId,
                                          group.orderLineId,
                                          receipt.receiptLineId
                                        );

                                        return (
                                          <Paper
                                            key={`${group.orderLineId}-${receipt.receiptLineId}`}
                                            variant="outlined"
                                            sx={{
                                              p: 1.25,
                                              ml: { sm: 2 },
                                              bgcolor: "background.paper",
                                              borderLeft: 3,
                                              borderLeftColor: "info.main",
                                            }}
                                          >
                                            <Stack
                                              direction={{ xs: "column", lg: "row" }}
                                              spacing={1.25}
                                              justifyContent="space-between"
                                              alignItems={{ xs: "stretch", lg: "center" }}
                                            >
                                              <Box sx={{ minWidth: 180 }}>
                                                <Stack
                                                  direction="row"
                                                  spacing={0.75}
                                                  alignItems="center"
                                                >
                                                  <Chip
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                    label="PZ"
                                                  />
                                                  <Typography variant="body2" fontWeight={700}>
                                                    {receipt.receiptNo || "—"}
                                                  </Typography>
                                                </Stack>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ display: "block", mt: 0.35 }}
                                                >
                                                  {t("Wiersz")} {receipt.receiptLineNo || "—"}
                                                </Typography>
                                              </Box>

                                              <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={1}
                                                alignItems={{ sm: "center" }}
                                              >
                                                <TextField
                                                  size="small"
                                                  type="number"
                                                  label={t("Ilość do zafakturowania")}
                                                  value={
                                                    quantityDrafts[draftKey] ??
                                                    String(receipt.qtyToInvoice ?? 0)
                                                  }
                                                  onChange={(event) =>
                                                    setQuantityDrafts((current) => ({
                                                      ...current,
                                                      [draftKey]: event.target.value,
                                                    }))
                                                  }
                                                  inputProps={{ min: 0, step: "any" }}
                                                  disabled={busy}
                                                  sx={{ width: { xs: "100%", sm: 220 } }}
                                                />
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  disabled={busy}
                                                  onClick={() => void saveQuantity(line, receipt)}
                                                >
                                                  {t("Zapisz ilość")}
                                                </Button>
                                                <Button
                                                  size="small"
                                                  color="error"
                                                  variant="text"
                                                  startIcon={<DeleteOutlineIcon />}
                                                  disabled={busy}
                                                  onClick={() =>
                                                    setPendingDelete({
                                                      kind: "single",
                                                      line,
                                                      orderLineId: group.orderLineId,
                                                      receiptLineId: receipt.receiptLineId,
                                                      label: `${t("przyjęcie")} ${receipt.receiptNo || "—"}`,
                                                    })
                                                  }
                                                >
                                                  {t("Usuń PZ")}
                                                </Button>
                                              </Stack>
                                            </Stack>
                                          </Paper>
                                        );
                                      })}
                                    </Stack>
                                  )}
                                </Stack>
                              </Paper>
                            );
                          })}

                          <Box>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<DeleteOutlineIcon />}
                              disabled={busy}
                              onClick={() =>
                                setPendingDelete({
                                  kind: "clear",
                                  line,
                                  label: `${t("wiersz")} ${line.lineNo ?? "—"}`,
                                })
                              }
                            >
                              {t("Usuń wszystkie dopasowania wiersza")}
                            </Button>
                          </Box>
                        </Stack>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => busyLineId === null && setPendingDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Potwierdź usunięcie dopasowania")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {pendingDelete?.kind === "clear"
              ? t(
                  "Ta operacja usunie wszystkie dopasowania ZZ/PZ z wybranego wiersza. Po usunięciu ostatniego dopasowania Business Central może również usunąć wymiary pochodzące z zamówienia."
                )
              : pendingDelete?.receiptLineId
              ? t("Czy usunąć wybrane przyjęcie PZ z dopasowania?")
              : t(
                  "Czy usunąć wybrane zamówienie ZZ wraz ze wszystkimi przypisanymi pod nim przyjęciami PZ?"
                )}
          </Typography>
          {pendingDelete && (
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              {pendingDelete.label}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDelete(null)}
            disabled={busyLineId !== null}
          >
            {t("Anuluj")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmDelete()}
            disabled={busyLineId !== null}
          >
            {busyLineId !== null ? t("Usuwanie...") : t("Usuń")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
